export type ServiceLevelRevisionType = 'working_days' | 'time_band' | 'lead_time'

export const SERVICE_LEVEL_REVISION_TYPE_SELECTIONS = [
  { label: '営業日', value: 'working_days' as const },
  { label: '時間帯区分', value: 'time_band' as const },
  { label: 'リードタイム', value: 'lead_time' as const },
]

/** 管理画面マルチセレクト用（月始まり。既存営業日マスタと同一） */
export const SERVICE_LEVEL_REVISION_WEEKDAY_SELECTIONS = [
  '月',
  '火',
  '水',
  '木',
  '金',
  '土',
  '日',
] as const

/** 注釈表示用の曜日順（日始まり） */
export const SERVICE_LEVEL_REVISION_WEEKDAY_DISPLAY_ORDER = [
  '日',
  '月',
  '火',
  '水',
  '木',
  '金',
  '土',
] as const

const VALID_WEEKDAYS = new Set<string>(SERVICE_LEVEL_REVISION_WEEKDAY_SELECTIONS)

export const SERVICE_LEVEL_REVISION_TIME_BAND_SELECTIONS = [
  { label: '0: 4区分', value: '0' },
  { label: '1: 3区分[無料]', value: '1' },
  { label: '2: 3区分[有料]', value: '2' },
  { label: '3: 指定不可', value: '3' },
  { label: '4: 4区分（2027-01-01〜）', value: '4' },
  { label: '5: 指定不可（2027-01-01〜 / JIT可）', value: '5' },
]

export type ServiceLevelRevisionRecord = {
  id?: string
  revision_type: ServiceLevelRevisionType | string
  pref_code: string
  city: string
  revision_date: string
  value_after?: string | null
  value_after_weekdays?: string[] | string | null
  value_after_lead_time_e?: string | null
  value_after_lead_time_fg?: string | null
}

export type MunicipalityKey = {
  prefCode: string
  city: string
}

export type ServiceLevelRevisionNotes = {
  workingDays: string | null
  timeBand: string | null
  leadTimeE: string | null
  leadTimeFg: string | null
}

const REVISION_TYPE_LABEL: Record<ServiceLevelRevisionType, string> = {
  working_days: '営業日',
  time_band: '時間帯区分',
  lead_time: 'リードタイム',
}

export const formatRevisionTypeLabel = (type: string | null | undefined): string => {
  if (!type) return ''
  return REVISION_TYPE_LABEL[type as ServiceLevelRevisionType] ?? type
}

export const formatRevisionDateForNote = (revisionDate: string): string => {
  return $core.$dayjs(revisionDate).format('YYYY年M月D日')
}

/**
 * MULTISELECT (CSV) / JSON 配列など複数形態の曜日を string[] に正規化する。
 * 許可曜日のみ残し、重複を除去する（順序は入力順のまま）。
 */
export const parseWeekdays = (weekdays: unknown): string[] => {
  if (weekdays == null || weekdays === '') {
    return []
  }

  let raw: string[] = []
  if (Array.isArray(weekdays)) {
    raw = weekdays.filter((v): v is string => typeof v === 'string' && v.length > 0)
  } else if (typeof weekdays === 'string') {
    const trimmed = weekdays.trim()
    if (!trimmed) return []
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) {
          raw = parsed.filter((v): v is string => typeof v === 'string' && v.length > 0)
        }
      } catch {
        raw = trimmed
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
      }
    } else {
      raw = trimmed
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    }
  }

  const seen = new Set<string>()
  const result: string[] = []
  for (const day of raw) {
    if (!VALID_WEEKDAYS.has(day) || seen.has(day)) continue
    seen.add(day)
    result.push(day)
  }
  return result
}

/** 注釈表示用に日始まり順へ並べ替え */
export const sortWeekdaysForDisplay = (weekdays: string[]): string[] => {
  const set = new Set(weekdays)
  return SERVICE_LEVEL_REVISION_WEEKDAY_DISPLAY_ORDER.filter((d) => set.has(d))
}

/** 管理画面一覧用に月始まり順へ並べ替え */
export const sortWeekdaysForAdminDisplay = (weekdays: string[]): string[] => {
  const set = new Set(weekdays)
  return SERVICE_LEVEL_REVISION_WEEKDAY_SELECTIONS.filter((d) => set.has(d))
}

export const formatWeekdaysForNote = (weekdays: string[]): string => {
  return sortWeekdaysForDisplay(weekdays).join('')
}

/** 管理画面一覧用: 週6 (月水木金土日) / 週7 */
export const formatWeekdaysForAdminLabel = (weekdays: unknown): string => {
  const parsed = parseWeekdays(weekdays)
  if (parsed.length === 0) return ''
  if (parsed.length === 7) return '週7'
  return `週${parsed.length} (${sortWeekdaysForAdminDisplay(parsed).join('')})`
}

/**
 * 改定日前のみ表示対象（改定当日以降は非表示）
 */
export const isRevisionBeforeDate = (revisionDate: string, asOfDate: string): boolean => {
  if (!revisionDate || !asOfDate) return false
  return revisionDate > asOfDate
}

export const filterFutureRevisions = <T extends { revision_date: string }>(
  revisions: T[],
  asOfDate: string,
): T[] => {
  return revisions.filter((r) => isRevisionBeforeDate(r.revision_date, asOfDate))
}

export const matchesMunicipality = (
  revision: Pick<ServiceLevelRevisionRecord, 'pref_code' | 'city'>,
  municipality: MunicipalityKey | null | undefined,
): boolean => {
  if (!municipality?.prefCode || !municipality?.city) return false
  return (
    revision.pref_code === municipality.prefCode && revision.city === municipality.city
  )
}

/**
 * 同一種別・該当市区郡のうち、改定日が最も近い未来1件を返す
 */
export const findNearestFutureRevision = (
  revisions: ServiceLevelRevisionRecord[],
  municipality: MunicipalityKey | null | undefined,
  revisionType: ServiceLevelRevisionType,
  asOfDate: string,
): ServiceLevelRevisionRecord | null => {
  if (!municipality?.prefCode || !municipality?.city) return null

  const matched = revisions
    .filter(
      (r) =>
        r.revision_type === revisionType &&
        matchesMunicipality(r, municipality) &&
        isRevisionBeforeDate(r.revision_date, asOfDate),
    )
    .sort((a, b) => a.revision_date.localeCompare(b.revision_date))

  return matched[0] ?? null
}

/**
 * 複数市区郡（発着）のいずれかに一致する、種別ごとの直近未来改定
 */
export const findNearestFutureRevisionForMunicipalities = (
  revisions: ServiceLevelRevisionRecord[],
  municipalities: Array<MunicipalityKey | null | undefined>,
  revisionType: ServiceLevelRevisionType,
  asOfDate: string,
): ServiceLevelRevisionRecord | null => {
  const candidates = municipalities
    .map((m) => findNearestFutureRevision(revisions, m, revisionType, asOfDate))
    .filter((r): r is ServiceLevelRevisionRecord => !!r)
    .sort((a, b) => a.revision_date.localeCompare(b.revision_date))

  return candidates[0] ?? null
}

/**
 * 営業日注釈
 * - 週7: ※{日付}より週7営業となります
 * - それ以外: ※{日付}より週{N}営業 ({曜日}) となります
 */
export const formatWorkingDaysRevisionNote = (
  revisionDate: string,
  weekdays: unknown,
): string | null => {
  if (!revisionDate) return null
  const parsed = parseWeekdays(weekdays)
  if (parsed.length === 0) return null

  const dateLabel = formatRevisionDateForNote(revisionDate)
  if (parsed.length === 7) {
    return `※${dateLabel}より週7営業となります`
  }
  return `※${dateLabel}より週${parsed.length}営業 (${formatWeekdaysForNote(parsed)}) となります`
}

/**
 * 時間帯区分注釈（暫定文言・後で差し替え）
 * valueAfter は将来の文言で使う想定のため現状未使用
 */
export const formatTimeBandRevisionNote = (
  revisionDate: string,
  _valueAfter?: string | null,
): string | null => {
  if (!revisionDate) return null
  const dateLabel = formatRevisionDateForNote(revisionDate)
  return `※${dateLabel}より時間帯区分が変更となります`
}

/**
 * リードタイム注釈（暫定文言・後で差し替え）
 */
export const formatLeadTimeRevisionNote = (revisionDate: string): string | null => {
  if (!revisionDate) return null
  const dateLabel = formatRevisionDateForNote(revisionDate)
  return `※${dateLabel}よりリードタイムが変更となります`
}

export const buildRevisionNotesForMunicipality = (
  revisions: ServiceLevelRevisionRecord[],
  municipality: MunicipalityKey | null | undefined,
  asOfDate: string,
): Pick<ServiceLevelRevisionNotes, 'workingDays' | 'timeBand'> => {
  const workingDaysRev = findNearestFutureRevision(
    revisions,
    municipality,
    'working_days',
    asOfDate,
  )
  const timeBandRev = findNearestFutureRevision(
    revisions,
    municipality,
    'time_band',
    asOfDate,
  )

  return {
    workingDays: workingDaysRev
      ? formatWorkingDaysRevisionNote(
          workingDaysRev.revision_date,
          workingDaysRev.value_after_weekdays,
        )
      : null,
    timeBand: timeBandRev
      ? formatTimeBandRevisionNote(timeBandRev.revision_date, timeBandRev.value_after)
      : null,
  }
}

export const buildLeadTimeRevisionNotes = (
  revisions: ServiceLevelRevisionRecord[],
  municipalities: Array<MunicipalityKey | null | undefined>,
  asOfDate: string,
): Pick<ServiceLevelRevisionNotes, 'leadTimeE' | 'leadTimeFg'> => {
  const leadTimeRev = findNearestFutureRevisionForMunicipalities(
    revisions,
    municipalities,
    'lead_time',
    asOfDate,
  )
  if (!leadTimeRev) {
    return { leadTimeE: null, leadTimeFg: null }
  }

  const note = formatLeadTimeRevisionNote(leadTimeRev.revision_date)
  const hasE = !!(leadTimeRev.value_after_lead_time_e || '').trim()
  const hasFg = !!(leadTimeRev.value_after_lead_time_fg || '').trim()

  return {
    leadTimeE: hasE ? note : null,
    leadTimeFg: hasFg ? note : null,
  }
}

export const municipalityFromZipMaster = (
  zipMaster: { prefCode?: string | null; city?: string | null } | null | undefined,
): MunicipalityKey | null => {
  if (!zipMaster?.prefCode || !zipMaster?.city) return null
  return { prefCode: zipMaster.prefCode, city: zipMaster.city }
}
